import PersonAddAltSharpIcon from "@mui/icons-material/PersonAddAltSharp";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import { ITableProps, kaReducer, Table } from "ka-table";
import {
  deselectAllVisibleRows,
  deselectRow,
  hideLoading,
  selectAllVisibleRows,
  selectRow,
  selectRowsRange,
  showLoading,
  updateData,
} from "ka-table/actionCreators";
import { DataType } from "ka-table/enums";
import { ChildComponents } from "ka-table/models";
import { ICellTextProps, IHeadCellProps } from "ka-table/props";
import { DispatchFunc } from "ka-table/types";
import { kaPropsUtils } from "ka-table/utils";
import { useContext, useEffect, useState } from "react";

import DialogHeader from "components/DialogBox/DialogHeader";
import TableFooter from "components/TableFooter";
import { applicationDetails } from "dto/appDto";
import user from "dto/userDto";
import { grantAccessToApp } from "services/appsService";
import { getAllUser } from "services/userServices";
import { selectAuth } from "slices/authSlice";
import { addUser } from "slices/userSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import dialogContext, { DialogType } from "utilities/dialogContext";
import { getRoleName, Log } from "utilities/helper";

import "pages/users/components/UserListTable.scss";

type GrantAccessDialogProps = {
  app: applicationDetails;
  open: boolean;
  handleClose: () => void;
};

type CustomCellProps = {
  rowData: ICellTextProps["rowData"];
  app: applicationDetails;
  fetchData: () => void;
};

const SelectionCell: React.FC<ICellTextProps> = ({
  rowKeyValue,
  dispatch,
  isSelectedRow,
  selectedRows,
}) => {
  return (
    <input
      type="checkbox"
      checked={isSelectedRow}
      onChange={(event: any) => {
        if (event.nativeEvent.shiftKey) {
          dispatch(selectRowsRange(rowKeyValue, [...selectedRows].pop()));
        } else if (event.currentTarget.checked) {
          dispatch(selectRow(rowKeyValue));
        } else {
          dispatch(deselectRow(rowKeyValue));
        }
      }}
    />
  );
};

const SelectionHeader: React.FC<IHeadCellProps> = ({
  dispatch,
  areAllRowsSelected,
}) => {
  return (
    <input
      type="checkbox"
      checked={areAllRowsSelected}
      onChange={(event) => {
        if (event.currentTarget.checked) {
          dispatch(selectAllVisibleRows()); // also available: selectAllFilteredRows(), selectAllRows()
        } else {
          dispatch(deselectAllVisibleRows()); // also available: deselectAllFilteredRows(), deselectAllRows()
        }
      }}
    />
  );
};

const CustomCell = ({ rowData, app, fetchData }: CustomCellProps) => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectAuth);
  var {
    setDialogType,
    setShowDialog,
    setTitle,
    setMessage,
    setOnClickOk,
    setOnClickCancel,
  } = useContext(dialogContext);

  function grantAccess(userid: number) {
    setTitle("Grant Access");
    setMessage(
      `User with identifier "${rowData.column2}" will be granted access to "${app.applicationName}". Are you sure?`
    );
    setDialogType(DialogType.CONFIRMATION);
    setOnClickCancel(() => () => setShowDialog(false));

    setOnClickOk(() => () => {
      grantAccessToApp(auth.authToken, app.id, [userid]).then((response) => {
        var title = "";
        var message = "";
        if (response[0]) {
          var result = response[0];
          message = result.message;
          if (result.success) {
            title = "Success!";
            var attachedUser: user = {
              id: rowData.id,
              userIdentifier: rowData.column2,
              name: rowData.column1,
              email: rowData.column3,
              phoneNumber: "",
              address: "",
              role: rowData.column4,
              createdAt: "",
            };
            dispatch(addUser(attachedUser));
            fetchData();
          } else {
            title = "Error!";
          }
        } else {
          title = "Error";
          message = "Please try again later";
          var error = response[1];
          Log(error.message);
        }

        setTitle(title);
        setMessage(message);
        setDialogType(DialogType.MESSAGE);
        setOnClickOk(() => () => setShowDialog(false));
      });
    });

    setShowDialog(true);
  }

  return (
    <Button
      variant="contained"
      color="info"
      startIcon={<PersonAddAltSharpIcon fontSize="small" />}
      onClick={() => grantAccess(rowData.id)}
      sx={{
        position: "inherit",
        m: 0.5,
      }}
    >
      Grant Access
    </Button>
  );
};

export default function GrantAccessDialog({
  app,
  open,
  handleClose,
}: GrantAccessDialogProps) {
  let auth = useAppSelector(selectAuth);
  const [userList, setUserList] = useState<user[]>();

  const [searchString, setsearchString] = useState<string>("");
  const [totalUserCount, setTotalUserCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);

  function fetchData() {
    getAllUser(
      auth.authToken,
      app.id.toString(),
      searchString,
      true,
      page + 1,
      limit
    ).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setUserList(result.data);
          setTotalUserCount(result.totalCount);
          updateTableData(result.data);
        } else {
          updateTableData([]);
          Log(result.message);
        }
      } else {
        updateTableData([]);
        var error = response[1];
        Log(error.message);
      }
    });
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, auth, page, limit]);

  const ChildComponents: ChildComponents = {
    noDataRow: {
      content: () => "No Data Found",
    },

    pagingIndex: {
      elementAttributes: ({ isActive }) => ({
        className: `page-item waves-effect ${isActive ? "active" : ""}`,
      }),
    },

    pagingSize: {
      elementAttributes: ({ value }) => ({
        className: `page-item waves-effect ${value === option.paging?.pageSize ? "active" : ""
          }`,
      }),
    },

    pagingSizes: {
      elementAttributes: () => ({
        className: "pagination",
      }),
    },

    pagingPages: {
      elementAttributes: () => ({
        className: "pagination",
      }),
    },

    cellText: {
      content: (props) => {
        switch (props.column.key) {
          case "selection-cell":
            return <SelectionCell {...props} />;
          case "column5":
            return (
              <CustomCell
                rowData={props.rowData}
                app={app}
                fetchData={fetchData}
              />
            );
        }
      },
    },

    filterRowCell: {
      content: (props) => {
        if (props.column.key === "selection-cell") {
          return <></>;
        }
      },
    },
    headCell: {
      content: (props) => {
        if (props.column.key === "selection-cell") {
          return (
            <SelectionHeader
              {...props}
              areAllRowsSelected={
                kaPropsUtils.areAllVisibleRowsSelected(option) &&
                kaPropsUtils.getSelectedData(option).length > 0
              }
            // areAllRowsSelected={kaPropsUtils.areAllFilteredRowsSelected(tableProps)}
            />
          );
        }
      },
    },
  };

  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down("md"));

  let tableOption: ITableProps = {
    columns: [
      {
        key: "selection-cell",
        width: 50,
      },
      {
        key: "column1",
        title: "Name",
        width: 200,
        dataType: DataType.String,
      },
      {
        key: "column2",
        title: "User Identifier",
        width: 250,
        dataType: DataType.String,
      },
      {
        key: "column4",
        title: "Role",
        width: 100,
        dataType: DataType.String,
      },
      {
        key: "column5",
        title: "Action",
        width: 170,
        style: { textAlign: "center" },
      },
    ],
    data: userList,
    height: md ? 300 : 500,
    rowKeyField: "id",
  };

  const [option, changeOptions] = useState(tableOption);
  const dispatch: DispatchFunc = (action) => {
    changeOptions((prevState: ITableProps) => kaReducer(prevState, action));
  };

  const updateTableData = (userData: user[]) => {
    dispatch(showLoading());
    var dataArray = userData.map((ListItem) => ({
      //column0: ListItem.id,
      column1: ListItem.name,
      column2: ListItem.userIdentifier,
      //column3: ListItem.email,
      column4: getRoleName(ListItem.role),
      id: ListItem.id,
    }));

    dispatch(updateData(dataArray));
    dispatch(hideLoading());
  };

  const appDispatch = useAppDispatch();
  var {
    setDialogType,
    setShowDialog,
    setTitle,
    setMessage,
    setVerificationText,
    setOnClickOk,
    setOnClickCancel,
  } = useContext(dialogContext);

  function grantAccessToSelectedUser(userList: any) {
    setTitle("Grant Access");

    var message = "";
    userList.forEach((element: any) => {
      message = message + "<br/>" + element["column2"];
    });
    setMessage(
      `The following user(s) will be granted access to "${app.applicationName}". Are you sure?${message}`
    );
    setVerificationText("grant");
    setDialogType(DialogType.CONFIRMATION);
    setOnClickCancel(() => () => setShowDialog(false));

    setOnClickOk(() => () => {
      var userIdList: any = [];
      userList.forEach((element: any) => {
        userIdList.push(element["id"]);
      });
      grantAccessToApp(auth.authToken, app.id, userIdList).then((response) => {
        var title = "";
        var message = "";
        if (response[0]) {
          var result = response[0];
          message = result.message;
          if (result.success) {
            title = "Success!";
            userList.forEach((element: any) => {
              var attachedUser: user = {
                id: element.id,
                userIdentifier: element.column2,
                name: element.column1,
                email: "",
                phoneNumber: "",
                address: "",
                role: element.column4,
                createdAt: "",
              };
              appDispatch(addUser(attachedUser));
            });
            fetchData();
          } else {
            title = "Error!";
          }
        } else {
          title = "Error";
          message = "Please try again later";
          var error = response[1];
          Log(error.message);
        }

        setTitle(title);
        setMessage(message);
        setDialogType(DialogType.MESSAGE);
        setOnClickOk(() => () => setShowDialog(false));
      });
    });

    setShowDialog(true);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        style: {
          minHeight: "60%",
          maxHeight: "90%",
          minWidth: "60%",
          maxWidth: "80%",
        },
      }}
    >
      <DialogHeader
        title={"Grant Access of " + app.applicationName}
        onClose={handleClose}
      />
      <DialogContent>
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          direction="row"
          pl={1}
          pr={1}
          pb={2}
        >
          <Grid item justifyContent={"left"}>
            <TextField
              id="user"
              value={searchString}
              sx={{ width: "50vh" }}
              placeholder="Search User"
              variant="outlined"
              InputProps={{ endAdornment: <SearchIcon /> }}
              onChange={(event: any) => {
                setsearchString(event.target.value);
              }}
            />
          </Grid>
          <Grid item justifyContent={"right"}>
            <Button
              variant="contained"
              color="info"
              disabled={kaPropsUtils.getSelectedData(option).length <= 0}
              startIcon={<PersonAddAltSharpIcon fontSize="small" />}
              onClick={() =>
                grantAccessToSelectedUser(kaPropsUtils.getSelectedData(option))
              }
              sx={{
                position: "inherit",
                m: 0.5,
              }}
            >
              Grant Access
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ pl: 1, pr: 1, pb: 1 }}>
          <div className="ka-table-user">
            <Table
              {...option}
              dispatch={dispatch}
              childComponents={ChildComponents}
            />
          </div>
          <TableFooter
            page={page}
            limit={limit}
            dataLength={totalUserCount}
            setPage={setPage}
            setLimit={setLimit}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
